import Icon from 'common/Icon'
import Modal from 'common/Modal'
import { DIM_GREY } from 'constants/colors'
import './index.scss'
import { stateAbbreviations } from 'app/SeFriendly/validateUsaState'
import { StimulantStateBucket } from 'hooks/bookings/useStimulantBucket'
import { useRouter } from 'next/router'
import { Choose, When } from '@/utility_components/ControlStatements'

type PsychNoticeModalProps = {
  showModal: boolean
  onHide: () => void
  isSupportUser?: boolean
  bucket: StimulantStateBucket | null
}

const MODAL_TITLE_SUPPORT = 'Psychiatry Stimulants Notice'
const MODAL_TITLE_USER = 'Prescription regulations in your state'

const PsychNoticeModal: React.FC<PsychNoticeModalProps> = ({
  showModal,
  onHide,
  isSupportUser = false,
  bucket,
}) => {
  const router = useRouter()
  const { query } = router

  const currentState =
    typeof query?.state === 'string' ? query.state.trim().toUpperCase() : ''
  const fullStateName = stateAbbreviations[currentState]
  const isInPersonOfferedState = bucket === 'inPersonOffered'
  const isInPersonNotOfferedState = bucket === 'inPersonNotOffered'

  const hasContent = isInPersonOfferedState || isInPersonNotOfferedState

  if (!hasContent) {
    return null
  }

  return (
    <Modal
      show={showModal}
      onHide={onHide}
      size="md"
      modalType="centered-content-no-overflow"
      contentClassOverride="PsychNoticeModal"
    >
      <div className="PsychNoticeModal__container">
        <div>
          <Choose>
            <When condition={isInPersonOfferedState}>
              <>
                <div className="PsychNoticeModal__title">
                  {isSupportUser ? MODAL_TITLE_SUPPORT : MODAL_TITLE_USER}
                </div>
                <div className="PsychNoticeModal__text">
                  {isSupportUser ? (
                    <>
                      It looks like you&apos;re searching for psychiatry care
                      for ADHD in <b>{fullStateName}</b>, a condition
                      that&apos;s sometimes treated with stimulant medications.
                      <br />
                      <br />
                      <b>
                        {fullStateName} regulations require an in-person visit
                        for stimulant prescriptions. When booking, select
                        &quot;In-Person&quot; as the visit type (in addition to
                        the ADHD filter).
                      </b>
                      <br />
                      <br />
                      If you&apos;re looking for medication management that
                      doesn&apos;t involve stimulants, you&apos;re welcome to
                      book a virtual session.
                      <br />
                      <br />
                      <b>Note:</b> The provider will determine the right
                      treatment plan during the visit. Prescriptions are based
                      on the individual needs and are not guaranteed.
                    </>
                  ) : (
                    <>
                      It looks like you&apos;re searching for psychiatry care
                      for ADHD in <b>{fullStateName}</b>, a condition
                      that&apos;s sometimes treated with stimulant medications.
                      <br />
                      <br />
                      {fullStateName} regulations require an in-person visit for
                      stimulant prescriptions. When booking, select
                      &quot;In-Person&quot; as your visit type.
                      <br />
                      <br />
                      If you&apos;re looking for medication management that
                      doesn&apos;t involve stimulants, you&apos;re welcome to
                      book a virtual session.
                      <br />
                      <br />
                      <b>Note:</b> Your provider will determine the right
                      treatment plan during your visit. Prescriptions are based
                      on your individual needs and are not guaranteed.
                    </>
                  )}
                </div>
              </>
            </When>
            <When condition={isInPersonNotOfferedState}>
              <>
                <div className="PsychNoticeModal__title">
                  {isSupportUser ? MODAL_TITLE_SUPPORT : MODAL_TITLE_USER}
                </div>
                <div className="PsychNoticeModal__text">
                  {isSupportUser ? (
                    <>
                      It looks like you&apos;re searching for psychiatry care
                      for ADHD in <b>{fullStateName}</b>, a condition
                      that&apos;s sometimes treated with stimulant medications.
                      <br />
                      <br />
                      <b>
                        {fullStateName} regulations require an in-person visit
                        for stimulant prescriptions, and we don&apos;t currently
                        offer in-person psychiatry there.
                      </b>{' '}
                      We&apos;re actively expanding our psychiatric care – check
                      back soon.
                    </>
                  ) : (
                    <>
                      It looks like you&apos;re searching for psychiatry care
                      for ADHD in <b>{fullStateName}</b>, a condition
                      that&apos;s sometimes treated with stimulant medications.
                      <br />
                      <br />
                      {fullStateName} regulations require an in-person visit for
                      stimulant prescriptions, and we don&apos;t currently offer
                      in-person psychiatry there. We&apos;re actively expanding
                      our psychiatric care, so check back soon if you&apos;re
                      looking for this option.
                      <br />
                      <br />
                      If you&apos;re looking for medication management that
                      doesn&apos;t involve stimulants, or you aren&apos;t sure
                      what your treatment might include, you can book a virtual
                      psychiatry session. Your provider will help determine
                      what&apos;s right for you.
                    </>
                  )}
                </div>
              </>
            </When>
          </Choose>
        </div>

        <button className="PsychNoticeModal__closeIconBtn" onClick={onHide}>
          <Icon icon="lightCross" className="fa-md" fill={DIM_GREY} />
        </button>
      </div>
    </Modal>
  )
}

export default PsychNoticeModal
